import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { noop } from 'rxjs';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { RxUtils } from '@utils';
import { authService } from '@services';
import { Box, Grid, IconButton, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { AuthLayout, FormikSubmitButton, FormikTextFieldProps, FormikTextfield } from '@components/common';
import { SadFaceEmojiIcon } from '@components/icons';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';

const InputStyle: FormikTextFieldProps['sx'] = { minWidth: 300, maxWidth: 300 };

type FormFields = Extendable<{
	email: string;
	code: string;
	codeSent: boolean;
}>;

export const VerifyUser: FC = () => {
	const { t } = useTranslation('common');
	const { email: emailFromQueryParam = '' } = useParams<{ email: string }>();
	const navigate = useNavigate();

	const [codeResendInProgress, setCodeResendInProgress] = useState(false);
	const onResendCode = (email: string) => {
		if (email) {
			setCodeResendInProgress(true);
			RxUtils.promisify(authService.resendConfirmationCode(email), noop, noop, () => setCodeResendInProgress(false));
		}
	};

	const formik = useFormik<FormFields>({
		initialValues: { email: emailFromQueryParam, code: '' },
		validateOnChange: true,
		enableReinitialize: true,
		validationSchema: yup.object({
			email: yup.string().email(t('validation.email-invalid')).required(t('validation.email-required')),
			codeSent: yup.boolean(),
			code: yup.string().when('codeSent', {
				is: true,
				then: yup.string().min(6, t('validation.code-length-error')).required(t('validation.code-required')),
				otherwise: yup.string().nullable()
			})
		}),
		onSubmit: ({ code, codeSent, email }, api) => {
			if (codeSent) {
				RxUtils.promisify(
					authService.confirmRegistration(email, code),
					() => navigate('/portal'),
					RxUtils.noop,
					() => api.setSubmitting(false)
				);
			} else {
				RxUtils.promisify(
					authService.resendConfirmationCode(email),
					() => api.setFieldValue('codeSent', true),
					RxUtils.noop,
					() => api.setSubmitting(false)
				);
			}
		}
	});

	return (
		<AuthLayout>
			<Stack
				direction="column"
				spacing={{ xs: 2, md: 10 }}
				sx={{ flexGrow: 1, paddingX: 4, alignItems: 'center', justifyContent: 'center' }}
			>
				<Stack direction="column" alignItems="center">
					<SadFaceEmojiIcon sx={{ width: 48, height: 48, display: { xs: 'none', md: 'unset' } }} />
					<Typography
						variant="h2"
						sx={{
							marginTop: { xs: 0, md: 4 },
							fontSize: (theme) => ({ xs: theme.typography.h4.fontSize, md: theme.typography.h2.fontSize })
						}}
					>
						{t('verify-user.title')}
					</Typography>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
						<Typography
							sx={{ fontSize: (theme) => ({ xs: theme.typography.caption.fontSize, md: theme.typography.body1.fontSize }) }}
						>
							{t('verify-user.caption')}
						</Typography>
					</Stack>
				</Stack>
				<Box component="form" onSubmit={formik.handleSubmit} sx={{ width: 'fit-content' }}>
					<Grid container direction="row" spacing={{ xs: 1, md: 4 }} sx={{ width: { xs: 1.0, md: 650 }, marginX: 'auto' }}>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikTextfield
								formikApi={formik}
								name="email"
								disabled={!!emailFromQueryParam}
								title={t('email')}
								placeholder={t('email')}
								sx={InputStyle}
							/>
						</Grid>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikTextfield
								formikApi={formik}
								name="code"
								title={
									<Stack direction="row" alignItems="center" spacing={2}>
										<Typography variant="inherit">{t('code')}</Typography>
										{formik.values.codeSent ? (
											<Tooltip
												open
												arrow
												placement="top"
												title={<Typography>{t('invitation.code-tooltip')}</Typography>}
											>
												<InfoOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
											</Tooltip>
										) : null}
									</Stack>
								}
								disabled={codeResendInProgress || !formik.values.codeSent}
								placeholder={t('code')}
								sx={InputStyle}
								autoFocus
								InputProps={{
									endAdornment: (
										<InputAdornment position="end" sx={{ color: 'inherit' }}>
											<IconButton
												onClick={() => onResendCode(emailFromQueryParam)}
												disabled={codeResendInProgress || !formik.values.codeSent}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												<SyncProblemIcon color="inherit" />
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
						</Grid>
						<Grid item xs={12} sx={{ marginTop: { xs: 3, md: 10 }, display: 'flex', alignItems: 'center' }}>
							<FormikSubmitButton color="primary" formik={formik}>
								{t(formik.values.codeSent ? 'verify-user.verify-button' : 'verify-user.send-code-button')}
							</FormikSubmitButton>
						</Grid>
					</Grid>
				</Box>
			</Stack>
		</AuthLayout>
	);
};
